import { Service, ServiceWithDetails, ServiceImage, ServicePricing, AddonService, CleaningEquipment, CustomerRequirement } from '../types/service';
import { API_BASE_URL } from './api';

// Log the API base URL for debugging
console.log('ServiceService - Using API Base URL:', API_BASE_URL);

// Services

export const getServices = async (): Promise<Service[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services`);
    if (!response.ok) throw new Error('Failed to fetch services');

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const services = result.data?.services || result.data || result.services || result || [];

    // Transform the data to match the expected format
    return Array.isArray(services) ? services.map((service: any) => ({
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at
    })) : [];
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

export const getServiceById = async (id: number): Promise<ServiceWithDetails> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/${id}`);
    if (!response.ok) throw new Error(`Failed to fetch service with id ${id}`);

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const service = result.data?.service || result.data || result.service || result;

    if (!service) {
      throw new Error(`Service with id ${id} not found`);
    }

    // Transform the data to match the expected format
    return {
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  } catch (error) {
    console.error(`Error fetching service with id ${id}:`, error);
    throw error;
  }
};

export const createService = async (service: Omit<Service, 'service_id'>): Promise<Service> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(service)
    });
    if (!response.ok) throw new Error('Failed to create service');

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const createdService = result.data?.service || result.data || result.service || result;

    if (!createdService) {
      throw new Error('Service creation failed - no service data returned');
    }

    // Transform the data to match the expected format
    return {
      service_id: createdService.service_id || createdService.id,
      category_id: createdService.category_id,
      name: createdService.service_name || createdService.name,
      description: createdService.description,
      duration: createdService.duration_minutes || createdService.duration,
      base_price: createdService.base_price || createdService.price,
      discount_price: createdService.discount_price,
      image_url: createdService.image_url,
      is_active: Boolean(createdService.is_active || createdService.status),
      display_order: createdService.display_order || 0,
      created_at: createdService.created_at,
      updated_at: createdService.updated_at
    };
  } catch (error) {
    console.error('Error creating service:', error);
    throw error;
  }
};

export const updateService = async (id: number, service: Partial<Service>): Promise<Service> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/${id}/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(service)
    });
    if (!response.ok) throw new Error(`Failed to update service with id ${id}`);

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const updatedService = result.data?.service || result.data || result.service || result;

    if (!updatedService) {
      throw new Error('Service update failed - no service data returned');
    }

    // Transform the data to match the expected format
    return {
      service_id: updatedService.service_id || updatedService.id,
      category_id: updatedService.category_id,
      name: updatedService.service_name || updatedService.name,
      description: updatedService.description,
      duration: updatedService.duration_minutes || updatedService.duration,
      base_price: updatedService.base_price || updatedService.price,
      discount_price: updatedService.discount_price,
      image_url: updatedService.image_url,
      is_active: Boolean(updatedService.is_active || updatedService.status),
      display_order: updatedService.display_order || 0,
      created_at: updatedService.created_at,
      updated_at: updatedService.updated_at
    };
  } catch (error) {
    console.error(`Error updating service with id ${id}:`, error);
    throw error;
  }
};

export const deleteService = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error(`Failed to delete service with id ${id}`);
  } catch (error) {
    console.error(`Error deleting service with id ${id}:`, error);
    throw error;
  }
};

// Service Images

export const getServiceImages = async (serviceId: number): Promise<ServiceImage[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/${serviceId}/images`);
    if (!response.ok) throw new Error(`Failed to fetch images for service ${serviceId}`);
    return response.json();
  } catch (error) {
    console.error(`Error fetching images for service ${serviceId}:`, error);
    throw error;
  }
};
export const uploadServiceImage = async (serviceId: number, file: File): Promise<ServiceImage> => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${API_BASE_URL}/services/${serviceId}/images`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) throw new Error('Failed to upload service image');
    return response.json();
  } catch (error) {
    console.error('Error uploading service image:', error);
    throw error;
  }
};
export const updateServicePricing = async (
  serviceId: number, 
  pricing: Omit<ServicePricing, 'pricing_id' | 'service_id' | 'created_at' | 'updated_at'>[]
): Promise<ServicePricing[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/${serviceId}/pricing/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pricing)
    });
    if (!response.ok) throw new Error(`Failed to update pricing for service ${serviceId}`);
    return response.json();
  } catch (error) {
    console.error(`Error updating pricing for service ${serviceId}:`, error);
    throw error;
  }
};

// Addon Services

export const getAddonServices = async (): Promise<AddonService[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/addon-services`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to fetch addon services');
    }

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data.addonServices' property
    const addonServices = result.data?.addonServices || [];

    console.log('Addon services fetched:', addonServices);

    // Transform the data to match the expected format
    return addonServices.map((service: any) => ({
      addon_id: service.addon_id,
      name: service.name,
      description: service.description,
      category: service.category,
      base_price: parseFloat(service.base_price) || 0,
      image_url: service.image_url,
      duration: service.duration,
      status: service.status === 1,
      display_order: service.display_order,
      created_at: service.created_at,
      updated_at: service.updated_at
    }));
  } catch (error) {
    console.error('Error fetching addon services:', error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export const getAddonServiceById = async (id: number): Promise<AddonService> => {
  try {
    const response = await fetch(`${API_BASE_URL}/addon-services/${id}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to fetch addon service with id ${id}`);
    }

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data.addonService' property
    const service = result.data?.addonService;

    if (!service) {
      throw new Error(`Addon service with id ${id} not found`);
    }

    console.log('Addon service fetched by ID:', service);

    // Transform the data to match the expected format
    return {
      addon_id: service.addon_id,
      name: service.name,
      description: service.description,
      category: service.category,
      base_price: parseFloat(service.base_price) || 0,
      image_url: service.image_url,
      duration: service.duration,
      status: service.status === 1,
      display_order: service.display_order,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  } catch (error) {
    console.error(`Error fetching addon service with id ${id}:`, error);
    throw error;
  }
};



export const createAddonService = async (formData: FormData): Promise<AddonService> => {
  try {
    // Convert FormData to plain object
    const data: { [key: string]: any } = {};
    formData.forEach((value, key) => {
      data[key] = value;
    });

    // Use the correct endpoint URL based on the API_BASE_URL
    const endpoint = API_BASE_URL.endsWith('/admin')
      ? `${API_BASE_URL}/addon-services`
      : `${API_BASE_URL}/admin/addon-services`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Create error response:', errorText);
      let errorMessage = 'Failed to create addon service';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in a 'data.addonService' property or direct
    const service = result.data?.addonService || result.data || result;

    // Transform the data to match the expected format
    return {
      addon_id: service.addon_id,
      name: service.name,
      description: service.description,
      category: service.category,
      base_price: parseFloat(service.base_price) || 0,
      image_url: service.image_url,
      duration: service.duration,
      status: service.status === 1,
      display_order: service.display_order,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  } catch (error) {
    console.error('Error creating addon service:', error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export const updateAddonService = async (id: number, formData: FormData): Promise<AddonService> => {
  try {
    // Convert FormData to plain object and map to backend field names
    const data: any = {};
    formData.forEach((value, key) => {
      // Map frontend field names to backend field names
      if (key === 'base_price') {
        data['price'] = value;
      } else {
        data[key] = value;
      }
    });

    console.log('Sending update data:', data);
    
    // Use the correct endpoint URL based on the API_BASE_URL
    const endpoint = API_BASE_URL.endsWith('/admin') 
      ? `${API_BASE_URL}/addon-services/${id}/update`
      : `${API_BASE_URL}/admin/addon-services/${id}/update`;
      
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(data as any).toString()
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update error response:', errorText);
      let errorMessage = 'Failed to update addon service';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    const result = await response.json();

    // Handle the API response structure - data might be wrapped in a 'data.addonService' property or direct
    const service = result.data?.addonService || result.data || result;

    // Transform the data to match the expected format
    return {
      addon_id: service.addon_id,
      name: service.name,
      description: service.description,
      category: service.category,
      base_price: parseFloat(service.base_price) || 0,
      image_url: service.image_url,
      duration: service.duration,
      status: service.status === 1,
      display_order: service.display_order,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  } catch (error) {
    console.error(`Error updating addon service with id ${id}:`, error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export const deleteAddonService = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/addon-services/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to delete addon service');
    }
  } catch (error) {
    console.error(`Error deleting addon service with id ${id}:`, error);
    throw error;
  }
};

// Cleaning Equipment

export const getCleaningEquipment = async (): Promise<CleaningEquipment[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cleaning-equipment`);
    if (!response.ok) throw new Error('Failed to fetch cleaning equipment');

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data.equipment' property
    const equipment = result.data?.equipment || [];

    console.log('Cleaning equipment fetched:', equipment);
    return equipment;
  } catch (error) {
    console.error('Error fetching cleaning equipment:', error);
    throw error;
  }
};

export const getCleaningEquipmentById = async (id: number): Promise<CleaningEquipment> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cleaning-equipment/${id}`);
    if (!response.ok) throw new Error('Failed to fetch cleaning equipment');

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in a 'data.equipment' property or just 'data'
    const equipment = result.data?.equipment || result.data;

    if (!equipment) {
      throw new Error(`Equipment with id ${id} not found`);
    }

    console.log('Cleaning equipment fetched by ID:', equipment);
    return equipment;
  } catch (error) {
    console.error('Error fetching cleaning equipment:', error);
    throw error;
  }
};

export const createCleaningEquipment = async (formData: FormData): Promise<CleaningEquipment> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cleaning-equipment`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to create equipment');
    }

    const data = await response.json();
    return {
      ...data,
      status: data.status === 1 || data.status === '1' ? 1 : 0,
      quantity_available: data.quantity_available || 0
    };
  } catch (error) {
    console.error('Error creating equipment:', error);
    throw error;
  }
};

export const updateCleaningEquipment = async (id: number, formData: FormData): Promise<CleaningEquipment> => {
  try {
    // Get the current status from form data and convert to string for comparison
    const status = formData.get('status');
    const statusString = status ? status.toString() : '0';

    // Always ensure status is set as '1' or '0'
    // If status is null/undefined, default to '0' (unchecked)
    const statusValue = statusString === '1' || statusString === 'true' ? '1' : '0';
    formData.set('status', statusValue);

    const response = await fetch(`${API_BASE_URL}/cleaning-equipment/${id}`, {
      method: 'PUT',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to update equipment');
    }

    const data = await response.json();
    return {
      ...data,
      status: data.status === 1 || data.status === '1' ? 1 : 0,
      quantity_available: data.quantity_available || 0
    };
  } catch (error) {
    console.error(`Error updating equipment with id ${id}:`, error);
    throw error;
  }
};

export const deleteCleaningEquipment = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cleaning-equipment/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete cleaning equipment');
  } catch (error) {
    console.error('Error deleting cleaning equipment:', error);
    throw error;
  }
};

// Customer Requirements

export const getCustomerRequirements = async (): Promise<CustomerRequirement[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements`);
    if (!response.ok) throw new Error('Failed to fetch customer requirements');
    return response.json();
  } catch (error) {
    console.error('Error fetching customer requirements:', error);
    throw error;
  }
};

export const getCustomerRequirementById = async (id: number): Promise<CustomerRequirement> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements/${id}`);
    if (!response.ok) throw new Error('Failed to fetch customer requirement');
    return response.json();
  } catch (error) {
    console.error('Error fetching customer requirement:', error);
    throw error;
  }
};

export const createCustomerRequirement = async (requirement: Omit<CustomerRequirement, 'requirement_id'>): Promise<CustomerRequirement> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requirement),
    });
    if (!response.ok) throw new Error('Failed to create customer requirement');
    return response.json();
  } catch (error) {
    console.error('Error creating customer requirement:', error);
    throw error;
  }
};

export const updateCustomerRequirement = async (id: number, requirement: Partial<CustomerRequirement>): Promise<CustomerRequirement> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements/${id}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requirement),
    });
    if (!response.ok) throw new Error('Failed to update customer requirement');
    return response.json();
  } catch (error) {
    console.error('Error updating customer requirement:', error);
    throw error;
  }
};

export const deleteCustomerRequirement = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete customer requirement');
  } catch (error) {
    console.error('Error deleting customer requirement:', error);
    throw error;
  }
};

export const getServiceRequirements = async (serviceId: number): Promise<CustomerRequirement[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/services/${serviceId}/requirements`);
    if (!response.ok) throw new Error('Failed to fetch service requirements');
    return response.json();
  } catch (error) {
    console.error('Error fetching service requirements:', error);
    throw error;
  }
};

export const updateServiceRequirements = async (
  serviceId: number,
  requirementIds: number[]
): Promise<{ service_id: number; requirement_id: number }[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/${serviceId}/requirements/update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requirement_ids: requirementIds }),
      }
    );
    if (!response.ok) {
      throw new Error('Failed to update service requirements');
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching service details:', error);
    throw error;
  }
};
